import React, { memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  Box,
  ButtonIcon,
  FooterActionBar,
  Icon,
  Menu,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';

import {
  A_KEY_DS,
  A_KEY_DT,
  A_KEY_EN,
  A_KEY_OS,
  A_KEY_RO,
  CF_NEU_STL,
  l,
  type MessageState,
  SERVICE_IDENTITY_DELETE_ACTION,
  SERVICE_IDENTITY_DISABLE_ACTION,
  SERVICE_IDENTITY_ENABLE_ACTION,
  SERVICE_IDENTITY_OWNER_ACTION,
  SERVICE_IDENTITY_ROTATE_ACTION,
} from '../../../../shared/constants';
import {
  type ServiceAccountOwners,
  type ServiceAccountState,
  type ServiceIdSPItemSA,
} from '../../../../shared/types';
import { serviceIdentityActions } from '../../data-store/serviceIdentitySlice';

import './ServiceIdentityListAction.css';

interface ServiceIdentityListActionProps {
  serviceIdentityId: number;
  policyId?: number;
  isSidePanel?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  isDisabled: boolean;
  showEnableAction?: boolean;
  serviceAccounts?: ServiceAccountState[]; // Used to determine if Rotate action should be shown in list view
  currentOwners?: ServiceAccountOwners[]; // Used to determine if Rotate action should be shown in list view
}

/**
 * Renders action controls (enable/disable/delete) for a service identity.
 * Displays as footer buttons in a side panel or as a dropdown menu in a list
 * row.
 *
 * @param props - {@link ServiceIdentityListActionProps}
 * @param props.serviceIdentityId - ID of the service identity to act on
 * @param props.policyId - ID of the associated policy
 * @param props.isSidePanel - When true, renders footer action bar layout
 * @param props.isError - Disables actions when the parent is in an error state
 * @param props.isLoading - Disables actions while the parent is loading
 * @param props.isDisabled - Current disabled state of the service identity;
 *   determines whether the enable or disable option is shown
 * @returns The action controls for the service identity
 */
function ServiceIdentityListAction({
  serviceIdentityId,
  policyId,
  isSidePanel,
  isError,
  isLoading,
  isDisabled,
  showEnableAction,
  serviceAccounts,
  currentOwners,
}: ServiceIdentityListActionProps) {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Utils
  const handleAction = useCallback(
    (so: MenuOption) => {
      if (so.value === A_KEY_RO) {
        dispatch(
          serviceIdentityActions.setActionManager({
            isOpen: true,
            id: serviceIdentityId,
            action: so.value,
            policyId,
            currentServiceAccounts:
              serviceAccounts as unknown as ServiceIdSPItemSA[],
          })
        );

        return;
      }

      if (so.value === A_KEY_OS) {
        dispatch(
          serviceIdentityActions.setActionManager({
            isOpen: true,
            id: serviceIdentityId,
            action: so.value,
            policyId,
            currentOwners,
          })
        );

        return;
      }

      dispatch(
        serviceIdentityActions.setActionManager({
          isOpen: true,
          id: serviceIdentityId,
          action: so.value,
          policyId,
        })
      );
    },
    [dispatch, serviceIdentityId, policyId, serviceAccounts, currentOwners]
  );

  if (isSidePanel) {
    return (
      <FooterActionBar
        size='large'
        className='ServiceIdentitySP-footerActionBar'>
        <div />
        <Box tag='div' className='ServiceIdentitySP-footerContainer'>
          {showEnableAction ? (
            <Box tag='div' className='ServiceIdentitySP-footerActionWrapper'>
              <ButtonIcon
                size='large'
                kind='outlinedSecondary'
                icon={<Icon kind='Check' size='xSmall' />}
                ariaLabel={intl.formatMessage({
                  id: 'H9XqT5',
                  defaultMessage: 'Enable',
                  description: 'Enable',
                })}
                onClick={() => handleAction({ label: '', value: A_KEY_EN })}
                isDisabled={isLoading ?? isError}
              />
              <Typography kind='body3' color={CF_NEU_STL}>
                <FormattedMessage
                  id='H9XqT5'
                  defaultMessage='Enable'
                  description='Enable'
                />
              </Typography>
            </Box>
          ) : (
            <Box tag='div' className='ServiceIdentitySP-footerActionWrapper'>
              <ButtonIcon
                size='large'
                kind='outlinedSecondary'
                icon={<Icon kind='Reject' size='xSmall' />}
                ariaLabel={intl.formatMessage({
                  id: 'l5pJhm',
                  defaultMessage: 'Disable',
                  description: 'Disable',
                })}
                onClick={() => handleAction({ label: '', value: A_KEY_DS })}
                isDisabled={isLoading ?? isError}
              />
              <Typography kind='body3' color={CF_NEU_STL}>
                <FormattedMessage
                  id='l5pJhm'
                  defaultMessage='Disable'
                  description='Disable'
                />
              </Typography>
            </Box>
          )}

          <Box tag='div' className='ServiceIdentitySP-footerActionWrapper'>
            <ButtonIcon
              size='large'
              kind='outlinedSecondary'
              icon={<Icon kind='Delete' size='xSmall' />}
              ariaLabel={intl.formatMessage({
                id: 'qviIFp',
                defaultMessage: 'Delete',
                description: 'Delete',
              })}
              onClick={() => handleAction({ label: '', value: A_KEY_DT })}
              isDisabled={isLoading ?? isError}
            />
            <Typography kind='body3' color={CF_NEU_STL}>
              <FormattedMessage
                id='qviIFp'
                defaultMessage='Delete'
                description='Delete'
              />
            </Typography>
          </Box>
        </Box>
      </FooterActionBar>
    );
  }

  return (
    <div className='ServiceIdentityListAction--isListWrapper'>
      <Menu
        isPortalEnabled
        isOutsideClickEnabled
        options={[
          SERVICE_IDENTITY_OWNER_ACTION,
          ...(policyId !== undefined ? [SERVICE_IDENTITY_ROTATE_ACTION] : []),
          ...(isDisabled
            ? [SERVICE_IDENTITY_ENABLE_ACTION]
            : [SERVICE_IDENTITY_DISABLE_ACTION]),
          SERVICE_IDENTITY_DELETE_ACTION,
        ].map((action) => ({
          ...action,
          label: intl.formatMessage(l[action.label as MessageState]),
        }))}
        onChange={handleAction}
        trigger={
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={intl.formatMessage({
              id: 'Zf9051',
              defaultMessage: 'Actions',
              description: 'Actions',
            })}
            icon={<Icon kind='MoreVertical' size='medium' />}
          />
        }
      />
    </div>
  );
}

ServiceIdentityListAction.defaultProps = {
  policyId: undefined,
  isSidePanel: false,
  isError: false,
  isLoading: false,
  showEnableAction: false,
  serviceAccounts: undefined,
  currentOwners: undefined,
};

export default memo(ServiceIdentityListAction);
